/*
	This is a reducer that ONLY HOUSES ANALYTICS calls.
	It contains no internal state.
	It only subscribes to the actions in the signup ducks file,
	and composes the state to
*/

import Analytics from "./util/Analytics";
import { prettifyData } from "./util/helpers";
import { FIELDS_MAP, UNIT_FIELDS } from "apps/profile_details/util/constants";

import {
	SAVE_FIELD_DATA_SUCCESS,
} from "ducks/profile_details";

// ------------------------- helpers -------------------------
const _fieldsMap = {
	[FIELDS_MAP.EDUCATION_LEVEL_AND_SCHOOL]: "education",
	[FIELDS_MAP.OCCUPATION]: "occupation",
	[FIELDS_MAP.RELIGION_VALUE]: "religion",
	[FIELDS_MAP.RELIGION_MODIFIER]: "religion seriousness",
	[FIELDS_MAP.CHILDREN_HAVE]: "having kids",
	[FIELDS_MAP.CHILDREN_WANT]: "wanting kids",
	[FIELDS_MAP.DIET]: "diet",
	[FIELDS_MAP.SMOKING]: "smoking",
	[FIELDS_MAP.DRINKING]: "drinking",
	[FIELDS_MAP.DRUGS]: "drugs",
	[FIELDS_MAP.HEIGHT]: "height",
	[FIELDS_MAP.ETHNICITIES]: "ethnicity",
	[FIELDS_MAP.GENDER_TAGS]: "genders",
	[FIELDS_MAP.NAME]: "first name",
	[FIELDS_MAP.BIRTHDATE]: "date of birth",
	[FIELDS_MAP.LOCATION]: "reported location",
	[FIELDS_MAP.UNDERLYING_GENDER]: "underlying gender",
};

const _dataMap = {
	[FIELDS_MAP.EDUCATION_LEVEL_AND_SCHOOL]: "education",
	[FIELDS_MAP.OCCUPATION]: "occupation",
	[FIELDS_MAP.RELIGION_VALUE]: "religion",
	[FIELDS_MAP.RELIGION_MODIFIER]: "religion seriousness",
	[FIELDS_MAP.CHILDREN_HAVE]: "has kids",
	[FIELDS_MAP.CHILDREN_WANT]: "wants kids",
	[FIELDS_MAP.DIET]: "diet",
	[FIELDS_MAP.SMOKING]: "smokes",
	[FIELDS_MAP.DRINKING]: "drinks",
	[FIELDS_MAP.DRUGS]: "drugs",
	[FIELDS_MAP.HEIGHT]: "height",
	[FIELDS_MAP.ETHNICITIES]: "ethnicity",
	[FIELDS_MAP.GENDER_TAGS]: "gender descriptors",
	[FIELDS_MAP.NAME]: "firstname",
	[FIELDS_MAP.UNDERLYING_GENDER]: "gender",
	[FIELDS_MAP.BIRTHDATE]: "date of birth",
};

const _valueMap = {
	[FIELDS_MAP.EDUCATION_MODIFIER]: {
		"&mdash;": "completed",
	},

	[FIELDS_MAP.EDUCATION_VALUE]: {
		"two_year_college": "two-year college",
	},

	[FIELDS_MAP.CHILDREN_HAVE]: {
		"has": "has kid(s)",
		"doesnt_have": "doesn’t have kids",
	},

	[FIELDS_MAP.CHILDREN_WANT]: {
		"might_want": "might want kids",
		"wants": "wants kids",
		"doesnt_want": "doesn’t want kids",
	},

	[FIELDS_MAP.DRINKING]: {
		"socially": "sometimes",
		"not_at_all": "never",
	},

	[FIELDS_MAP.SMOKING]: {
		"yes": "often",
		"no": "never",
	},

	[FIELDS_MAP.RELIGION_MODIFIER]: {
		"and_its_important": "and it’s important",
		"but_its_not_important": "but it’s not important",
		"and_laughing_about_it": "and laughing about it",
	},

	[FIELDS_MAP.ETHNICITIES]: {
		"hispanic_latin": "hispanic / latin",
	},
};

function _parseData(fields, currentField) {
	const { data } = fields[currentField];

	if (fields[currentField].isSkipped) {
		return "prefer not to say";

		// if there's a special value, record it
	} else if (_valueMap[currentField] && _valueMap[currentField][data]) {
		return _valueMap[currentField][data];
	} else if (currentField === FIELDS_MAP.BIRTHDATE) {
		return `${data.month}/${data.day}/${data.year}`;
	} else {
		return prettifyData(data);
	}
}

// ////////////////////////
// REDUCER ///////////////
// //////////////////////

export default function profileDetails(state, action) {
	switch (action.type) {
		case SAVE_FIELD_DATA_SUCCESS: {
			const { fields, currentField, isMetric } = state;
			const metaData = {};

			const eventName = `Selected next on onboarding my ${_fieldsMap[currentField]}`;

			// parse location data
			if (currentField === FIELDS_MAP.LOCATION) {
				const { data } = fields[currentField];

				if (data.zip) {
					metaData["postal code"] = data.zip;
				} else if (data.country && data.city) {
					metaData.city = data.city;
					metaData.country = data.country;
				}

				// do edu and occ by hand because it's not great at multiple fields on the same detail
			} else if (currentField === FIELDS_MAP.EDUCATION_LEVEL_AND_SCHOOL) {
				const { data } = fields[currentField];

				metaData.school = data.schools[0].name;

				if (data.schools[0].educationid) {
					metaData.schoolId = data.schools[0].educationid;
				}

				metaData.level = data.value;

				if (!data.value || data.value === "&mdash;" && !metaData.school) {
					metaData.preferNotToSay = true;
				}
			} else if (currentField === FIELDS_MAP.OCCUPATION) {
				const { data } = fields[currentField];

				metaData.title = data.title;
				metaData.employer = data.employer;

				if (!data.title && !data.employer) {
					metaData.preferNotToSay = true;
				}
			} else if (Array.isArray(currentField)) {
				currentField.forEach((field) => {
					metaData[_dataMap[field]] = _parseData(fields, field);
				});
			} else {
				metaData[_dataMap[currentField]] = _parseData(fields, currentField);
			}

			// add in unit system, if the field's a unit field
			if (UNIT_FIELDS.indexOf(currentField) !== -1) {
				metaData["unit system"] = isMetric ? "metric" : "imperial";
			}

			Analytics.logUIEvent(
				eventName,
				{
					data: metaData,
				},
			);

			return;
		}

		default: {
			return;
		}
	}
}



// WEBPACK FOOTER //
// ./src/analytics/profile_details.js